"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.usePlacesService = exports.useValidateAddress = exports.cleanString = void 0;
const react_1 = require("react");
const usePlacesAutocompleteService_1 = __importDefault(require("react-google-autocomplete/lib/usePlacesAutocompleteService"));
const AddressAutocomplete_utils_1 = require("./AddressAutocomplete.utils");
function cleanString(str) {
    return str.trim().toLowerCase().replace(/ /g, "").replace(/,/g, "");
}
exports.cleanString = cleanString;
const useValidateAddress = (options = {}) => {
    const { placePredictions, getPlacePredictions, autocompleteSessionToken, isPlacePredictionsLoading } = (0, usePlacesAutocompleteService_1.default)({
        libraries: ["places", "geometry"],
        sessionToken: true,
        debounce: 500,
        language: "en",
    });
    const defaultAddress = options.defaultAddress ?? "";
    const [currentAddress, setCurrentAddress] = (0, react_1.useState)(defaultAddress);
    const isCurrentAddressDefault = currentAddress === defaultAddress;
    function isValid() {
        /**
         * If the value of the address hasn't changed from the default one,
         * then it is implied that the address is valid (was previously allowed to
         * be selected). Without this, on the first render, the `placePredictions`
         * will be empty and any address will be considered invalid.
         * */
        if (isCurrentAddressDefault) {
            return true;
        }
        // Some places descriptions are not in english and special characters do not allow simple comparison between strings
        const predictedPlace = placePredictions.find((place) => cleanString(place.description) === cleanString(currentAddress) || place.matched_substrings?.length >= 3);
        if (!predictedPlace) {
            return false;
        }
        return currentAddress !== "";
    }
    const validateAddress = (address, restricted = {}) => {
        let origin;
        let bounds;
        setCurrentAddress(address);
        if (restricted.area) {
            origin = new google.maps.LatLng(restricted.area.center.latitude, restricted.area.center.longitude);
            bounds = (0, AddressAutocomplete_utils_1.transformCenterWithRadiusToLatLngBounds)(origin, restricted.area.radiusInMeters);
        }
        getPlacePredictions({
            input: address,
            sessionToken: autocompleteSessionToken,
            componentRestrictions: {
                country: restricted.countries || null,
            },
            locationBias: bounds,
            origin,
            types: [],
        });
    };
    return {
        validateAddress,
        isValid: isPlacePredictionsLoading ? true : isValid() && !!currentAddress,
    };
};
exports.useValidateAddress = useValidateAddress;
// The `useGooglePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService"` has an unfortunate bug.
// It is likely that there is some kind of race condition inside the library. If we initialise the hook "too fast" `placesService` gets returned as `null` before it's filled in,
// and subsequent rerender of the hook is needed from our side (hook doesn't rerender on its own...).
// Thus we emloy this "hacky" fix.
const useHandlePlacesServiceInitializationBug = (placesService) => {
    const [attemptsToFetchPlacesService, setAttemptsToFetchPlacesService] = (0, react_1.useState)(0);
    (0, react_1.useEffect)(() => {
        if (!placesService && attemptsToFetchPlacesService <= 3) {
            setTimeout(() => {
                setAttemptsToFetchPlacesService((val) => val + 1);
            }, 100);
        }
    }, [placesService, attemptsToFetchPlacesService]);
};
const usePlacesService = ({ restrictedArea, restrictedCountries, placePredictionsTypes = ["street_address", "point_of_interest", "locality", "premise"], }) => {
    const { placesService, placePredictions, getPlacePredictions, isPlacePredictionsLoading, autocompleteSessionToken, getQueryPredictions, } = (0, usePlacesAutocompleteService_1.default)({
        sessionToken: true,
        debounce: 500,
        language: "en",
    });
    useHandlePlacesServiceInitializationBug(placesService);
    const getPlacePredictionsForValue = (value) => {
        let origin;
        let bounds;
        if (restrictedArea) {
            origin = new google.maps.LatLng(restrictedArea.center.latitude, restrictedArea.center.longitude);
            bounds = (0, AddressAutocomplete_utils_1.transformCenterWithRadiusToLatLngBounds)(origin, restrictedArea.radiusInMeters);
        }
        return getPlacePredictions({
            input: value,
            sessionToken: autocompleteSessionToken,
            componentRestrictions: {
                country: restrictedCountries || null,
            },
            locationBias: bounds,
            origin,
            types: placePredictionsTypes,
        });
    };
    return {
        getPlacePredictions: getPlacePredictionsForValue,
        placesService,
        placePredictions,
        isPlacePredictionsLoading,
        autocompleteSessionToken,
        getQueryPredictions,
    };
};
exports.usePlacesService = usePlacesService;
//# sourceMappingURL=AddressAutocomplete.hooks.js.map